/**
 * AI CONTEXT
 * Purpose: lightweight fulfillment/timeout helpers for cascading async data dependencies.
 * Exposes: useTimeout, useIsFulfilled, useIsAllFulfilled.
 * Used by: table/data hooks that join dependent docs.
 * Importance: prevents infinite loading when some linked docs are missing.
 * Refactor: add richer diagnostics output (expected vs actual IDs) for debugging.
 */
/**
 * CASCADING DATA HOOKS
 *
 * Utilities for tracking when cascading/dependent data is fully loaded
 *
 * Problem: Class loads with sessionIds, but sessions haven't loaded yet.
 * TanStack Query would say "not pending" but data isn't actually ready.
 *
 * Solution: Track "expected vs actual" - if class has 10 sessionIds,
 * we're not ready until we have 10 session documents.
 */

import { useEffect, useMemo, useRef, useState } from "react"

import { Uuid } from "#customTypes/globalTypes"
import { DataWithId } from "#customTypes/types"

/**
 * Returns true when either the condition is met OR the timeout elapses.
 * If resolvedEarly is true before timeout, the timer is cleared immediately.
 *
 * @param timeoutMs - Maximum wait time (default 3000)
 * @param resolvedEarly - Optional condition that short-circuits the wait
 */
export function useTimeout(
  timeoutMs: number = 3000,
  resolvedEarly?: boolean
): boolean {
  const [timedOut, setTimedOut] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (resolvedEarly) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    timerRef.current = setTimeout(() => {
      setTimedOut(true)
      timerRef.current = null
    }, timeoutMs)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [timeoutMs, resolvedEarly])

  return timedOut || !!resolvedEarly
}

/**
 * Check if array of IDs is fully fulfilled by checking if docs exist
 *
 * Important: This checks if we HAVE data for each ID.
 *
 * Use with a timeout to handle missing docs gracefully.
 */
export function useIsFulfilled<T extends DataWithId>(
  expectedIds: Uuid[] | undefined,
  actualDocs: T[] | undefined,
  timeoutMs: number = 1000
): boolean {
  const timedOut = useTimeout(timeoutMs)
  const isFulfilled = useMemo(() => {
    if (!expectedIds?.length) return true
    if (!actualDocs?.length) return false

    const actualDocIds = new Set(actualDocs.map((doc) => doc.id))
    return expectedIds?.every((id) => actualDocIds.has(id))
  }, [expectedIds, actualDocs])

  return isFulfilled || timedOut
}

/**
 * Aggregate multiple fulfillment checks
 * Returns true only when ALL are fulfilled
 */
export function useIsAllFulfilled(checks: boolean[]): boolean {
  return checks.every(Boolean)
}
